import React from 'react';

/**
 * Render a trusted, allowlisted inline HTML fragment from the checked-in
 * policy snapshot as React elements. Only <a href>, <strong>, <em> and <code>
 * are accepted; anything else throws so tests and the sync tool catch it.
 * Nothing is ever passed to innerHTML.
 */
const ALLOWED = { a: 'a', strong: 'strong', em: 'em', code: 'code' };
const TOKEN = /<\/?([a-zA-Z][a-zA-Z0-9]*)((?:\s+[^\s=>]+(?:="[^"]*")?)*)\s*\/?>/g;

export function isSafeHref(href) {
  if (!href) return false;
  if (href.startsWith('#')) return /^#[A-Za-z][\w-]*$/.test(href);
  try {
    const url = new URL(href);
    return url.protocol === 'https:' || url.protocol === 'mailto:';
  } catch (error) {
    return false;
  }
}

function decodeEntities(text) {
  const entities = { amp: '&', lt: '<', gt: '>', quot: '"', '#39': "'", nbsp: '\u00a0' };
  return text.replace(/&(amp|lt|gt|quot|#39|nbsp);/g, (_, entity) => entities[entity]);
}

export function parseInlineMarkup(html) {
  if (/<!--|<\?|<!\[/.test(html)) throw new Error('Comments are not allowed in policy markup.');
  const root = { children: [] };
  const stack = [root];
  let last = 0;
  let keyCounter = 0;
  TOKEN.lastIndex = 0;
  let match;
  while ((match = TOKEN.exec(html)) !== null) {
    const [token, rawName, rawAttrs] = match;
    const text = html.slice(last, match.index);
    if (text) stack[stack.length - 1].children.push(decodeEntities(text));
    last = match.index + token.length;

    const name = rawName.toLowerCase();
    if (!ALLOWED[name]) throw new Error(`Disallowed tag <${name}> in policy markup.`);
    if (token.startsWith('</')) {
      const node = stack.pop();
      if (!node || node.name !== name) throw new Error(`Mismatched closing tag </${name}>.`);
      continue;
    }
    const node = { name, props: { key: `n${keyCounter++}` }, children: [] };
    const attrs = rawAttrs.trim();
    if (attrs) {
      const attrRe = /([^\s=]+)(?:="([^"]*)")?/g;
      let attr;
      while ((attr = attrRe.exec(attrs)) !== null) {
        const [, attrName, value] = attr;
        if (name !== 'a' || attrName.toLowerCase() !== 'href') {
          throw new Error(`Disallowed attribute ${attrName} on <${name}>.`);
        }
        const href = value && decodeEntities(value);
        if (!isSafeHref(href)) throw new Error(`Unsafe href ${value}.`);
        node.props.href = href;
        if (!href.startsWith('#') && !href.startsWith('mailto:')) {
          node.props.target = '_blank';
          node.props.rel = 'noopener noreferrer';
        }
      }
    }
    if (name === 'a' && !node.props.href) throw new Error('<a> without href in policy markup.');
    stack[stack.length - 1].children.push(node);
    stack.push(node);
  }
  if (stack.length !== 1) throw new Error('Unclosed tag in policy markup.');
  const tail = html.slice(last);
  if (tail) root.children.push(decodeEntities(tail));
  return root.children;
}

function toReact(nodes) {
  return nodes.map((node) => {
    if (typeof node === 'string') return node;
    return React.createElement(ALLOWED[node.name], node.props, ...toReact(node.children));
  });
}

export default function InlineMarkup({ html }) {
  return <>{toReact(parseInlineMarkup(html))}</>;
}
